import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  IconButton,
  Typography,
  TextField,
  MenuItem,
  Switch,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { format } from "date-fns";
import axios from "axios";
import Swal from "sweetalert2";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Header from "../../components/Header";
import DataGridCustomToolbar from "../../components/DataGridCustomToolbar";
import {
  useGetPostListQuery,
  useUpdatePostMutation,
  useDeletePostMutation,
} from "../../state/api";

const mediaUrl = (path) =>
  path ? `${axios.defaults.baseURL}/${path}` : null;

const POST_TYPES = ["image", "video", "text", "status"];

const Post = () => {
  const theme = useTheme();
  const { data, isLoading } = useGetPostListQuery();
  const [updatePost] = useUpdatePostMutation();
  const [deletePost] = useDeletePostMutation();
  const [pageSize, setPageSize] = useState(20);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [previewSrc, setPreviewSrc] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [editForm, setEditForm] = useState({
    caption: "",
    type: "",
    status: "",
    emoji: "",
    checkIn: "",
  });
  const [editImage, setEditImage] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);
  const [editVideo, setEditVideo] = useState(null);

  const rows = (data && data.posts) || [];

  const openEdit = (post) => {
    setEditingPost(post);
    setEditForm({
      caption: post.caption || "",
      type: post.type || "",
      status: post.status || "",
      emoji: post.emoji || "",
      checkIn: post.checkIn || "",
    });
    setEditImage(null);
    setEditImagePreview(null);
    setEditVideo(null);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditImage(file);
      setEditImagePreview(URL.createObjectURL(file));
    }
  };

  const handleEditVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditVideo(file);
    }
  };

  const handleEditSave = async () => {
    try {
      await updatePost({
        postId: editingPost._id,
        ...editForm,
        image: editImage,
        video: editVideo,
      }).unwrap();
      toast.success("Post updated successfully");
      setEditingPost(null);
    } catch (error) {
      console.error("Error updating post", error);
      toast.error("Failed to update post");
    }
  };

  const toggleActive = async (post) => {
    try {
      await updatePost({ postId: post._id, isActive: !post.isActive }).unwrap();
      toast.success(post.isActive ? "Post deactivated" : "Post activated");
    } catch (error) {
      console.error("Error toggling post status", error);
      toast.error("Failed to update post status");
    }
  };

  const handleDelete = (postId) => {
    Swal.fire({
      title: "Delete this post?",
      text: "This permanently removes the post. This cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deletePost({ postId, tag: "delete" }).unwrap();
          toast.success("Post deleted successfully");
        } catch (error) {
          console.error("Error deleting post", error);
          toast.error("Failed to delete post");
        }
      }
    });
  };

  const columns = [
    {
      field: "image",
      headerName: "Media",
      flex: 0.6,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => {
        const src = mediaUrl(params.row.image);
        if (!src) return "No image";
        return (
          <Box
            component="img"
            src={src}
            alt="post"
            onClick={() => setPreviewSrc(src)}
            sx={{
              width: 48,
              height: 48,
              objectFit: "cover",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          />
        );
      },
    },
    {
      field: "fullname",
      headerName: "User",
      flex: 0.7,
      minWidth: 140,
      valueGetter: (params) => params.row.userId?.fullname || "Unknown user",
    },
    {
      field: "caption",
      headerName: "Caption",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => params.value || "N/A",
    },
    {
      field: "type",
      headerName: "Type",
      flex: 0.4,
      minWidth: 90,
    },
    {
      field: "totalLikes",
      headerName: "Likes",
      flex: 0.4,
      minWidth: 80,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.5,
      minWidth: 100,
    },
    {
      field: "isActive",
      headerName: "Active?",
      flex: 0.5,
      minWidth: 110,
      sortable: false,
      renderCell: (params) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Switch
            checked={Boolean(params.value)}
            onChange={() => toggleActive(params.row)}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color: "green" },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "green",
              },
            }}
          />
        </div>
      ),
    },
    {
      field: "createdAt",
      headerName: "Posted",
      flex: 0.6,
      minWidth: 120,
      renderCell: (params) => format(new Date(params.value), "yyyy-MM-dd"),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      minWidth: 110,
      sortable: false,
      renderCell: (params) => (
        <div onClick={(e) => e.stopPropagation()}>
          <IconButton color="info" onClick={() => openEdit(params.row)}>
            <EditIcon />
          </IconButton>
          <IconButton onClick={() => handleDelete(params.row._id)}>
            <DeleteIcon sx={{ color: "red" }} />
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <Box m="1.5rem 2.5rem">
      <ToastContainer />
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <Header title="USER POSTS" />
      </Box>

      <Box
        mt="40px"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: theme.palette.primary.success,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderTop: "none",
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${theme.palette.secondary[200]} !important`,
          },
        }}
      >
        <DataGrid
          loading={isLoading || !data}
          getRowId={(row) => row._id}
          rows={rows}
          columns={columns}
          rowHeight={64}
          pagination
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[20, 50, 100]}
          components={{ Toolbar: DataGridCustomToolbar }}
          componentsProps={{
            toolbar: { searchInput, setSearchInput, setSearch, columns, rowData: rows },
          }}
        />
      </Box>

      {/* Media preview dialog */}
      <Dialog
        open={Boolean(previewSrc)}
        onClose={() => setPreviewSrc(null)}
        maxWidth="md"
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
          },
        }}
      >
        <Box display="flex" justifyContent="flex-end">
          <IconButton color="error" onClick={() => setPreviewSrc(null)}>
            <CancelIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ pt: 0, textAlign: "center" }}>
          {previewSrc ? (
            <Box
              component="img"
              src={previewSrc}
              alt="post full"
              sx={{ maxWidth: "100%", maxHeight: "75vh", borderRadius: "8px" }}
            />
          ) : (
            <Typography>No image</Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit post dialog */}
      <Dialog
        open={Boolean(editingPost)}
        onClose={() => setEditingPost(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
          },
        }}
      >
        <Box display="flex" justifyContent="flex-end" pt={1} pr={1}>
          <IconButton color="error" onClick={() => setEditingPost(null)}>
            <CancelIcon />
          </IconButton>
        </Box>
        <DialogTitle sx={{ pt: 0 }}>Edit Post</DialogTitle>
        <DialogContent>
          <TextField
            label="Caption"
            name="caption"
            value={editForm.caption}
            onChange={handleEditChange}
            fullWidth
            multiline
            minRows={2}
            margin="normal"
          />
          <TextField
            select
            label="Type"
            name="type"
            value={editForm.type}
            onChange={handleEditChange}
            fullWidth
            margin="normal"
          >
            {POST_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Status"
            name="status"
            value={editForm.status}
            onChange={handleEditChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Emoji / Feeling"
            name="emoji"
            value={editForm.emoji}
            onChange={handleEditChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Check-in"
            name="checkIn"
            value={editForm.checkIn}
            onChange={handleEditChange}
            fullWidth
            margin="normal"
          />

          <Box mt={2}>
            <Typography sx={{ fontSize: "0.8rem", mb: 1 }}>Image</Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <Button variant="outlined" component="label">
                {editingPost?.image ? "Replace Image" : "Upload Image"}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleEditImageChange}
                />
              </Button>
              <Box
                component="img"
                src={
                  editImagePreview ||
                  mediaUrl(editingPost?.image) ||
                  undefined
                }
                alt="post preview"
                sx={{
                  width: 56,
                  height: 56,
                  objectFit: "cover",
                  borderRadius: "6px",
                  display:
                    editImagePreview || editingPost?.image ? "block" : "none",
                }}
              />
            </Box>
          </Box>

          <Box mt={2}>
            <Typography sx={{ fontSize: "0.8rem", mb: 1 }}>Video</Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <Button variant="outlined" component="label">
                {editingPost?.video ? "Replace Video" : "Upload Video"}
                <input
                  type="file"
                  accept="video/*"
                  hidden
                  onChange={handleEditVideoChange}
                />
              </Button>
              {editVideo && (
                <Typography sx={{ fontSize: "0.8rem" }}>
                  {editVideo.name}
                </Typography>
              )}
              {!editVideo && editingPost?.video && (
                <Typography sx={{ fontSize: "0.8rem" }}>
                  Current: {editingPost.video.split("/").pop()}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingPost(null)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleEditSave}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Post;
